import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import supportApi from "support/api/supportApi";
import { getOutbox, setOutbox } from "support/storage/supportStorage";
import {
  hydrateOutbox,
  markOutboxFailed,
  markOutboxSending,
  markOutboxSent,
  retryOutbox,
} from "stores/support/supportSlice";
import { selectSupportOutbox } from "stores/support/supportSelectors";

// A 4xx is the server rejecting this message and will reject it again; only a
// transport-level failure is worth retrying. 503 is what services/api reports
// for a dead or CORS-blocked response, which is exactly the retryable case.
export function isRetryable(status) {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

const MAX_ATTEMPTS = 6;

function backoffMs(attempts) {
  return Math.min(30000, 1000 * Math.pow(2, Math.max(0, attempts - 1)));
}

export function newTempId() {
  return "t" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// client_ref is capped at 36 chars server-side and is the idempotency key, so a
// retry of the same queued message can never double-post.
export function newClientRef() {
  return (Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12)).slice(0, 36);
}

export default function useOutboxPump(enabled) {
  const dispatch = useDispatch();
  const outbox = useSelector(selectSupportOutbox);
  const running = useRef(false);
  const timer = useRef(null);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) {
      return;
    }
    hydrated.current = true;
    const stored = getOutbox();
    if (stored.length) {
      // Anything caught mid-flight by a reload is pending again; the server
      // dedupes on client_ref if it did land.
      dispatch(
        hydrateOutbox(
          stored.map((item) => (item.status === "sending" ? { ...item, status: "pending" } : item))
        )
      );
    }
  }, [dispatch]);

  useEffect(() => {
    setOutbox(outbox);
  }, [outbox]);

  const pump = useCallback(async () => {
    if (running.current || !enabled) {
      return;
    }
    const next = outbox.find((m) => m.status === "pending");
    if (!next) {
      return;
    }
    running.current = true;
    dispatch(markOutboxSending(next.tempId));
    const response = await supportApi.postMessage(next.ref, {
      body: next.body,
      client_ref: next.client_ref,
    });
    running.current = false;

    if (response.status === 200) {
      dispatch(markOutboxSent(next.tempId));
      return;
    }
    if (isRetryable(response.status) && next.attempts + 1 < MAX_ATTEMPTS) {
      timer.current = setTimeout(() => {
        dispatch(retryOutbox(next.tempId));
      }, backoffMs(next.attempts + 1));
      return;
    }
    dispatch(markOutboxFailed({ tempId: next.tempId, status: response.status }));
  }, [dispatch, enabled, outbox]);

  useEffect(() => {
    pump();
  }, [pump]);

  // Coming back online is the one moment a queue built up offline can drain.
  useEffect(() => {
    const onOnline = () => pump();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [pump]);

  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    },
    []
  );
}
